import styled from "@emotion/styled";
import { ReloadOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Segmented, Select, Skeleton, Tabs, Typography } from "antd";
import { goBack, search } from "assets/icons/index";
import { useLocation, useNavigate } from "react-router-dom";
import ProductOnSale, { ProductChannelFilter } from "./ProductOnSale";
import { useGetInventoryByFactoryId, useGetProductList, useGetProductListApprove, useGetProductListInActive } from "hooks/useProduct";
import ProductApprove from "./ProductApprove";
import SearchProductForm from "./SearchProductForm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "store";
import ProductInventoryGrid from "./ProductInventoryGrid";
import InventorySkuEditModal from "./InventorySkuEditModal";
import {
  inventoryItemMatchesSearch,
  isInventorySkuItem,
  InventorySaleFilterMode,
  InventoryStockFilterMode,
  productHasSkuMatchingStockFilter,
  productMatchesInventorySaleFilter,
} from "./inventoryHelpers";
import {
  ManageProductMainView,
  ManageProductRestoreLocationState,
  ManageProductReturnState,
  getManageProductReturnSession,
  saveManageProductReturnSession,
} from "utils/session/manageProductReturnSession";

/** เลื่อนกลับไปตำแหน่งเดิม — เรียกซ้ำกัน Header ที่ scrollTo(0,0) ตอน mount */
const restorePageScroll = (y: number) => {
  if (!y) return;
  requestAnimationFrame(() => window.scrollTo(0, y));
  setTimeout(() => window.scrollTo(0, y), 200);
  setTimeout(() => window.scrollTo(0, y), 500);
};

const ManageProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAppliedRestoreRef = useRef(false);
  /** เปิด panel แรกครั้งเดียวตอนเข้ามุมมองคลัง — ไม่บังคับซ้ำเมื่อผู้ใช้ปิดทั้งหมดแล้ว */
  const hasInitializedInventoryExpandRef = useRef(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [productChannelFilter, setProductChannelFilter] =
    useState<ProductChannelFilter>("platform");
  // เปิดมาครั้งแรกหรือกดเมนูปกติ → default 'สินค้า' เสมอ
  // restore เฉพาะตอนกลับจากหน้าแก้ไขสินค้า (ผ่าน location.state หรือ went_to_edit flag)
  const [mainView, setMainView] = useState<string>('สินค้า');
  const [productTabKey, setProductTabKey] = useState("1");
  // ตัวกรอง "สถานะสต็อก" — limited = เปิดสต็อก (default), unlimited = ไม่เปิดสต็อก
  const [stockFilterMode, setStockFilterMode] = useState<InventoryStockFilterMode>("limited");
  const [inventorySaleFilter, setInventorySaleFilter] = useState<InventorySaleFilterMode>("all");
  /** คำค้นหาเฉพาะมุมมองคลัง (กดปุ่มค้นหาแล้วค่อยกรอง — แนวเดียวกับแท็บสินค้า) */
  const [inventorySearchInput, setInventorySearchInput] = useState<string>("");
  const [inventoryExpandedKeys, setInventoryExpandedKeys] = useState<string[]>([]);

  /** SKU ที่กำลังแก้ไขจากตารางคลัง (เปิด modal กำหนดสินค้า–ราคา/คลัง inline) */
  const [skuEditTarget, setSkuEditTarget] = useState<{
    productId: string;
    skuId: string;
  } | null>(null);

  const factoryId = useAppSelector((state) => state.auth.profile?.user?.role?.ref_id ?? '');

  const applyReturnState = useCallback((state: ManageProductReturnState) => {
    setMainView(state.mainView);
    if (state.productTabKey) {
      setProductTabKey(state.productTabKey);
    }
    if (state.stockFilterMode) {
      setStockFilterMode(state.stockFilterMode);
    }
    if (state.inventorySaleFilter) {
      setInventorySaleFilter(state.inventorySaleFilter);
    }
    if (state.inventorySearchInput != null) {
      setInventorySearchInput(state.inventorySearchInput);
    }
    if (state.searchInput != null) {
      setSearchInput(state.searchInput);
    }
    if (state.productChannelFilter) {
      setProductChannelFilter(state.productChannelFilter);
    }
    if (state.inventoryExpandedKeys?.length) {
      setInventoryExpandedKeys(state.inventoryExpandedKeys);
      hasInitializedInventoryExpandRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (hasAppliedRestoreRef.current) return;
    hasAppliedRestoreRef.current = true;
    const restore = location.state as ManageProductRestoreLocationState | null;
    if (restore?.restoreManageProduct && restore.returnState) {
      applyReturnState(restore.returnState);
      restorePageScroll(restore.returnState.scrollTop ?? 0);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    // กดปุ่ม browser back จากหน้าแก้ไขสินค้า (ไม่ผ่าน UI button) →
    // ตรวจ flag ที่ตั้งไว้ตอน navigate ออกไป เพื่อกันไม่ให้ restore ทุกครั้งที่ mount
    const wentToEdit = sessionStorage.getItem('manage_product_went_to_edit');
    if (wentToEdit) {
      sessionStorage.removeItem('manage_product_went_to_edit');
      const session = getManageProductReturnSession();
      if (session) {
        applyReturnState(session);
        restorePageScroll(session.scrollTop ?? 0);
      }
    }
    // ไม่มี flag → เปิดมาปกติ → default 'สินค้า' (ไม่ restore)
  }, [location.state, location.pathname, navigate, applyReturnState]);

  const buildReturnState = useCallback(
    (options?: { expandProductId?: string }): ManageProductReturnState => {
      const expandedKeys =
        options?.expandProductId != null
          ? Array.from(new Set([...inventoryExpandedKeys, options.expandProductId]))
          : inventoryExpandedKeys;

      return {
        mainView: mainView as ManageProductMainView,
        productTabKey,
        stockFilterMode,
        inventorySaleFilter,
        inventorySearchInput,
        searchInput,
        productChannelFilter,
        inventoryExpandedKeys: expandedKeys.length > 0 ? expandedKeys : undefined,
      };
    },
    [
      mainView,
      productTabKey,
      stockFilterMode,
      inventorySaleFilter,
      inventorySearchInput,
      searchInput,
      productChannelFilter,
      inventoryExpandedKeys,
    ],
  );

  const goToEditProduct = useCallback(
    (productId: string) => {
      const returnState: ManageProductReturnState = {
        ...buildReturnState({ expandProductId: productId }),
        scrollTop: window.scrollY,
      };
      saveManageProductReturnSession(returnState);
      // ตั้ง flag ว่า "กำลังจะไปหน้าแก้ไขสินค้า" เพื่อให้ตอนกดปุ่ม browser back กลับมา
      // manage-product จะรู้ว่าต้อง restore session ไม่ใช่ default ใหม่
      sessionStorage.setItem('manage_product_went_to_edit', '1');
      navigate(`/manage-product/edit/${productId}`, {
        state: {
          fromManageProduct: true,
          returnState,
        },
      });
    },
    [buildReturnState, navigate],
  );

  // จำตำแหน่ง scroll ปัจจุบัน + บันทึกสถานะหน้าเมื่อออกจากหน้า (ไปเมนูอื่น)
  const scrollYRef = useRef(0);
  const latestReturnStateRef = useRef<ManageProductReturnState | null>(null);
  useEffect(() => {
    const onScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    latestReturnStateRef.current = buildReturnState();
  }, [buildReturnState]);
  useEffect(() => {
    return () => {
      const s = latestReturnStateRef.current;
      if (s) {
        saveManageProductReturnSession({ ...s, scrollTop: scrollYRef.current });
      }
    };
  }, []);

  const activeProductListInput = useMemo(() => {
    const base = {
      is_active: true as const,
      is_approve: true as const,
      search: searchInput,
    };
    // if (productChannelFilter === "ext") {
    //   return { ...base, is_show_app: false };
    // }
     //return { ...base, is_show_app: true };

     return { ...base };
  }, [searchInput, productChannelFilter]);

  const {
    data: ProductListActive,
    isLoading: loadingProductListActive,
    refetch: refectchProductListActive,
  } = useGetProductList(activeProductListInput);
  const {
    data: ProductListInActive,
    isLoading: loadingProductListInActive,
    refetch: refectchProductListInActive,
  } = useGetProductListInActive({ is_active: false, is_approve: true, search:searchInput });

  const {
    data: ProductListApprove,
    isLoading: loadingProductListApprove,
    refetch: refectchProductListApprove,
  } = useGetProductListApprove({ is_approve: false, search:searchInput });

  const inventoryEnabled = mainView === 'คลัง' && !!factoryId;

  const {
    data: inventoryData,
    isPending: inventoryPending,
    isFetching: inventoryFetching,
    refetch: refetchInventory,
  } = useGetInventoryByFactoryId(factoryId, inventoryEnabled);

  const inventorySearchTrimmed = inventorySearchInput.trim().toLowerCase();

  // inventoryPending เป็น true เมื่อ query disabled (enabled=false) ด้วย — ต้องกั้นด้วย inventoryEnabled
  // ไม่งั้น factoryId ยังไม่พร้อม หรือ session restore ทำให้ enabled=false แต่ isPending=true → skeleton ไม่หยุด
  const loadingInventoryView =
    (inventoryPending && inventoryEnabled) || (inventoryFetching && !inventoryData);

  const inventoryProducts = useMemo(
    () => inventoryData?.products ?? [],
    [inventoryData?.products],
  );

  const skuInventoryProducts = useMemo(
    () => inventoryProducts.filter(isInventorySkuItem),
    [inventoryProducts],
  );

  const searchFilteredInventoryProducts = useMemo(() => {
    if (!inventorySearchTrimmed) return inventoryProducts;
    return inventoryProducts.filter((p) =>
      inventoryItemMatchesSearch(p, inventorySearchTrimmed),
    );
  }, [inventoryProducts, inventorySearchTrimmed]);

  const searchFilteredSkuProducts = useMemo(
    () => searchFilteredInventoryProducts.filter(isInventorySkuItem),
    [searchFilteredInventoryProducts],
  );

  const saleFilteredSkuProducts = useMemo(
    () =>
      searchFilteredSkuProducts.filter((p) =>
        productMatchesInventorySaleFilter(p, inventorySaleFilter),
      ),
    [searchFilteredSkuProducts, inventorySaleFilter],
  );

  const productsForInventory = useMemo(
    () =>
      saleFilteredSkuProducts
        .filter((p) => productHasSkuMatchingStockFilter(p, stockFilterMode))
        // เรียงตามชื่อสินค้าให้ตรงกับหน้า "จัดการสินค้า" (ProductOnSale ใช้ localeCompare ชื่อ)
        .slice()
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [saleFilteredSkuProducts, stockFilterMode],
  );

  const inventorySkuProductIds = useMemo(
    () => productsForInventory.map((p) => p.id),
    [productsForInventory],
  );

  const inventoryExpandSyncKey = useMemo(
    () =>
      `${stockFilterMode}|${inventorySaleFilter}|${inventorySearchTrimmed}|${inventorySkuProductIds.join(",")}`,
    [
      stockFilterMode,
      inventorySaleFilter,
      inventorySearchTrimmed,
      inventorySkuProductIds,
    ],
  );

  useEffect(() => {
    if (mainView !== "คลัง") return;
    if (hasInitializedInventoryExpandRef.current) {
      hasInitializedInventoryExpandRef.current = false;
      return;
    }
    setInventoryExpandedKeys(inventorySkuProductIds);
  }, [mainView, inventoryExpandSyncKey, inventorySkuProductIds]);

  const gridStockFilter =
    stockFilterMode === "all" ? null : stockFilterMode === "limited";

  const productTabItems = [
    {
      label: `ขายอยู่ (${ProductListActive?.products?.length ?? 0})`,
      key: "1",
      children: (
        <ProductOnSale
          isLoading={loadingProductListActive}
          data={ProductListActive}
          refetch={refectchProductListActive}
          refetchTo={refectchProductListInActive}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          channelFilter={productChannelFilter}
          onChannelFilterChange={setProductChannelFilter}
        />
      ),
    },
    {
      label: `กำลังตรวจสอบ (${ProductListApprove?.products?.length ?? 0})`,
      key: "3",
      children: (
        <>
          <ProductApprove
            isLoading={loadingProductListApprove}
            data={ProductListApprove}
            refetch={refectchProductListApprove}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        </>
      ),
    },
    {
      label: `ยังไม่เปิดการขาย (${ProductListInActive?.products?.length ?? 0})`,
      key: "5",
      children: (
        <>
          <ProductOnSale
            isLoading={loadingProductListInActive}
            data={ProductListInActive}
            refetch={refectchProductListInActive}
            refetchTo={refectchProductListActive}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/setting");
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>จัดการสินค้าและสต็อก</Typography.Title>
              <RightIconWrapper>
                <IconWrapper>
                  <SpanIcon>{search}</SpanIcon>
                </IconWrapper>
              </RightIconWrapper>
            </HeaderTabs>

            <SegmentedWrapper>
              <Segmented
                block
                options={['สินค้า', 'คลัง']}
                value={mainView}
                onChange={(v) => setMainView(v as string)}
              />
            </SegmentedWrapper>

            {mainView === 'สินค้า' && (
              <Tabs
                activeKey={productTabKey}
                onChange={setProductTabKey}
                style={{ height: "100%" }}
                items={productTabItems}
              />
            )}

            {mainView === 'คลัง' && (
              <InventorySection>
                <InventoryToolbar>
                  <InventorySearchSlot>
                    <SearchProductForm
                      searchInput={inventorySearchInput}
                      setSearchInput={setInventorySearchInput}
                    />
                  </InventorySearchSlot>
                  <InventoryToolbarRightActions>
                    <Button
                      icon={<ReloadOutlined />}
                      loading={inventoryFetching}
                      onClick={() => refetchInventory()}
                    >
                      รีโหลด
                    </Button>
                    <InventoryFilterField>
                      <InventoryFilterLabel>สถานะสต็อก</InventoryFilterLabel>
                      <InventorySaleSelect
                        value={stockFilterMode}
                        onChange={(v) =>
                          setStockFilterMode(v as InventoryStockFilterMode)
                        }
                        options={[
                          { value: "limited", label: "ใช้งานสต็อก" },
                          { value: "unlimited", label: "ไม่ได้ใช้สต็อก" },
                          { value: "all", label: "ทั้งหมด" },
                        ]}
                      />
                    </InventoryFilterField>
                    <InventoryFilterField>
                      <InventoryFilterLabel>สถานะการขาย</InventoryFilterLabel>
                      <InventorySaleSelect
                        value={inventorySaleFilter}
                        onChange={(v) =>
                          setInventorySaleFilter(v as InventorySaleFilterMode)
                        }
                        options={[
                          { value: "active", label: "เปิดการขาย" },
                          { value: "inactive", label: "ปิดการขาย" },
                          { value: "all", label: "ทั้งหมด" },
                        ]}
                      />
                    </InventoryFilterField>
                  </InventoryToolbarRightActions>
                </InventoryToolbar>
                {loadingInventoryView ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : skuInventoryProducts.length === 0 ? (
                  <EmptyInventory>
                    <Typography.Text type="secondary">ยังไม่มีสต็อก SKU</Typography.Text>
                  </EmptyInventory>
                ) : inventorySearchTrimmed && searchFilteredSkuProducts.length === 0 ? (
                  <EmptyInventory>
                    <Typography.Text type="secondary">ไม่พบรายการที่ตรงกับคำค้นหา</Typography.Text>
                  </EmptyInventory>
                ) : productsForInventory.length === 0 ? (
                  <EmptyInventory>
                    <Typography.Text type="secondary">ไม่มีรายการตามตัวกรอง</Typography.Text>
                  </EmptyInventory>
                ) : (
                  <InventoryListWrap>
                    <ProductInventoryGrid
                      products={productsForInventory.map((product) => ({
                        id: product.id,
                        name: product.name,
                        image_id: product.main_images?.[0],
                        image_url: product.main_images_url?.[0],
                        is_active: product.is_active,
                        variants: product.variants ?? [],
                      }))}
                      stockFilter={gridStockFilter}
                      onViewProduct={goToEditProduct}
                      onEditSku={(skuId, pid) =>
                        setSkuEditTarget({ skuId, productId: pid ?? "" })
                      }
                    />
                  </InventoryListWrap>
                )}
              </InventorySection>
            )}
          </Card>
        </Col>
      </Row>

      {/* แก้ไข SKU จากตารางคลัง — เปิด modal กำหนดสินค้า–ราคา/คลัง แบบ inline */}
      {skuEditTarget && skuEditTarget.productId && (
        <InventorySkuEditModal
          productId={skuEditTarget.productId}
          skuId={skuEditTarget.skuId}
          open
          onClose={() => setSkuEditTarget(null)}
        />
      )}
    </>
  );
};

export default ManageProduct;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const SegmentedWrapper = styled.div`
  padding: 8px 10px 4px;
`;

const InventorySection = styled.div`
  padding: 12px 10px;
`;

const InventoryToolbar = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 8px;
  padding: 0 0 10px;
  overflow-x: auto;
`;

/** ช่องค้นหา — ซ้ายสุดของกลุ่มควบคุมฝั่งขวา */
const InventorySearchSlot = styled.div`
  flex: 0 0 auto;
  width: 280px;
  max-width: 100%;

  & > div {
    margin-bottom: 0;
    width: 100%;
  }
`;

/** รีโหลด + ตัวกรอง — ชิดขวาของแถบ (ค้นหาอยู่ซ้าย) จัดชิดล่างให้ระดับ input ตรงกับช่องที่มี label */
const InventoryToolbarRightActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
`;

/** ตัวกรอง 1 ช่อง = label + select */
const InventoryFilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
`;

const InventoryFilterLabel = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  line-height: 1.1;
`;

const InventorySaleSelect = styled(Select)`
  min-width: 132px;
  flex-shrink: 0;
  &&.ant-select .ant-select-selector {
    height: 32px;
    align-items: center;
  }
`;

const InventoryPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-width: 0;
`;

const InventoryPanelHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
`;

const InventoryPanelHeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 8px;
`;

/** ปุ่มดูรายละเอียด — กรอบสี่เหลี่ยมให้มองเห็นชัด */
const InventoryViewDetailButton = styled(Button)`
  && {
    width: 36px;
    height: 36px;
    min-width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  && .anticon {
    font-size: 18px;
    color: #1677ff;
  }
  &&:hover,
  &&:focus {
    border-color: #1677ff;
    background: #f0f7ff;
  }
`;

const InventoryPanelTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const EmptyInventory = styled.div`
  padding: 48px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
`;

const InventoryListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

